<script setup lang="ts">
import { computed, ref } from 'vue';
import Velocity from 'velocity-animate';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useRoute } from 'vue-router';
import { DateTime } from 'luxon';
import BaseButton from '@/components/BaseButton.vue';
import CreateDemoAccountAction from '@/components/customer/CreateDemoAccountAction.vue';
import { useAuth } from '@/composables/firebase/auth';
import { useUpsale } from '@/composables/upsale';
import SubscriptionChangeModal from '@/components/payment/SubscriptionChangeModal.vue';
import { useFormatters } from '@/composables/formatters';

const { user } = useAuth();
const route = useRoute();
const { niceDateAndTime } = useFormatters();

const {
  shouldShowYearlyUpgradeMessage,
  shouldShowDemoAccountMessage,
  shouldShowTrialUpgradeMessage,
} = useUpsale();

// attention: could be a nice backend-feature
const customMessage = ref<string | undefined>();
const theme = computed(() => (customMessage.value ? {
  bg: 'var(--color-primary)',
  main: 'var(--color-secondary)',
  contrast: 'var(--color-secondary)',
  fontSize: 'var(--font-size-default)',
} : {
  bg: 'var(--color-accent-light)',
  main: 'var(--color-accent)',
  contrast: 'var(--color-accent-contrast)',
  fontSize: 'var(--font-size-m)',
}));

const trialExpiration = computed(() => {
  if (user.value?.profile?.trialUntil) {
    return niceDateAndTime(DateTime.fromISO(user.value.profile.trialUntil));
  }

  return null;
});

const isClosing = ref(false);
const showSubscriptionChangeModal = ref(false);

const isVisible = computed(() => {
  // ensure, that the bar is not visible if no user available
  if (!user.value) {
    return false;
  }

  // keep it disabled during the close animation
  if (isClosing.value) {
    return false;
  }

  // always hide as long as the current route is not known
  if (!route.name) {
    return false;
  }

  // hard disabled for some routes (e.g. checkout)
  if ((typeof route.meta.hideUpsaleBar === 'boolean' && route.meta.hideUpsaleBar)) {
    return false;
  }

  // visible, if any of the messages should be shown
  return shouldShowDemoAccountMessage.value
    || shouldShowTrialUpgradeMessage.value
    || shouldShowYearlyUpgradeMessage.value
    || customMessage.value;
});

const isClosable = computed(() => !shouldShowTrialUpgradeMessage.value);

const close = () => {
  if (user.value) {
    const flag = (() => {
      if (shouldShowDemoAccountMessage.value) {
        return 'demoAccount';
      }

      if (shouldShowYearlyUpgradeMessage.value) {
        return 'yearlyUpgrade';
      }

      return null;
    })();

    if (flag) {
      isClosing.value = true;
      user.value.updateProfile({
        seenUpsaleMessages: [
          ...(user.value.profile?.seenUpsaleMessages ?? []),
          flag,
        ],
      });
    }
  }
};

const leaveAnimation = (el: Element, done: () => void) => {
  Velocity(el, 'slideUp', {
    duration: 300,
    complete() {
      isClosing.value = false;
      done();
    },
  });
};

const enterAnimation = (el: Element, done: () => void) => {
  Velocity(el, 'slideDown', {
    duration: 300,
    complete() {
      done();
    },
  });
};

</script>

<template>
  <Transition
    :css="false"
    @enter="enterAnimation"
    @leave="leaveAnimation">
    <div
      v-if="isVisible"
      id="upsale-bar">
      <div class="flex justify-center">
        <template v-if="customMessage">
          <div v-html="customMessage" />
        </template>
        <template v-else>
          <div v-if="shouldShowDemoAccountMessage">
            <CreateDemoAccountAction class="ml-1">
              <template #action="{ createDemoAccount }">
                <i18n-t
                  scope="global"
                  keypath="UpsaleBar.mobileApp.content"
                  tag="p">
                  <template #cta>
                    <a
                      href=""
                      @click.prevent="createDemoAccount"
                      class="cta cta-secondary">
                      {{ $t('UpsaleBar.mobileApp.cta') }}</a>
                  </template>
                </i18n-t>
              </template>
            </CreateDemoAccountAction>
          </div>

          <div v-if="shouldShowTrialUpgradeMessage">
            <p>
              <font-awesome-icon
                class="mr-1"
                icon="triangle-exclamation" />
              <i18n-t
                scope="global"
                keypath="UpsaleBar.trialExpiration.content"
                tag="span">
                <template #expiration>
                  {{ trialExpiration }}
                </template>
                <template #cta>
                  <RouterLink
                    :to="{ name: 'choosePlan' }"
                    class="cta cta-secondary">
                    {{ $t('UpsaleBar.trialExpiration.cta') }}
                  </RouterLink>
                </template>
              </i18n-t>
            </p>
          </div>

          <div v-if="shouldShowYearlyUpgradeMessage">
            <i18n-t
              scope="global"
              keypath="UpsaleBar.yearlySubscription.content"
              tag="p">
              <template #cta>
                <a
                  href=""
                  @click.prevent="showSubscriptionChangeModal = true"
                  class="cta cta-secondary">
                  {{ $t('UpsaleBar.yearlySubscription.cta') }}</a>
              </template>
            </i18n-t>

            <SubscriptionChangeModal
              v-if="showSubscriptionChangeModal"
              @close="showSubscriptionChangeModal = false"
              initial-action="switchToYearly" />
          </div>

          <BaseButton
            v-if="isClosable"
            @click.prevent="close"
            class="close-cta btn btn--large btn--icon-only">
            <font-awesome-icon :icon="['far', 'xmark-circle']" />
          </BaseButton>
        </template>
      </div>
    </div>
  </Transition>
</template>

<style>
#upsale-bar {
  flex: none;
  padding: var(--space-3) var(--space-4);
  background: v-bind('theme.bg');
  border-bottom: 2px solid v-bind('theme.main');
  color: v-bind('theme.contrast');
  font-size: v-bind('theme.fontSize');
  font-weight: bold;
  text-align: center;
  position: relative;

  a {
    color: v-bind('theme.contrast');
  }

  .close-cta {
    position: absolute;
    top: var(--space-1);
    right: var(--space-1);
    width: 22px;
    height: 22px;
    padding: 0;
    color: v-bind('theme.main');

    &:hover {
      color: v-bind('theme.contrast');
    }
  }
}
</style>
